import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';

const EMPTY_FORM = {
  name: '',
  description: '',
  attachment: '',
  marksForMcq: '',
  minutes: '',
};

const EMPTY_MCQ_FORM = {
  question: '',
  questionImage: '',
  answer1: '',
  answer2: '',
  answer3: '',
  answer4: '',
  answer5: '',
  correctAnswer: '1',
};

export default function MyExam() {
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [loadingExams, setLoadingExams] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [coordinateCourses, setCoordinateCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [exams, setExams] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingName, setEditingName] = useState('');
  const [selectedAttachmentFile, setSelectedAttachmentFile] = useState(null);
  const [selectedAttachmentName, setSelectedAttachmentName] = useState('');
  const [isMcqModalOpen, setIsMcqModalOpen] = useState(false);
  const [activeMcqExam, setActiveMcqExam] = useState(null);
  const [mcqLoading, setMcqLoading] = useState(false);
  const [mcqSubmitting, setMcqSubmitting] = useState(false);
  const [mcqImageUploading, setMcqImageUploading] = useState(false);
  const [mcqs, setMcqs] = useState([]);
  const [mcqForm, setMcqForm] = useState(EMPTY_MCQ_FORM);
  const [editingMcqBulkKey, setEditingMcqBulkKey] = useState('');
  const [selectedMcqImageName, setSelectedMcqImageName] = useState('');
  const [mcqManagerTab, setMcqManagerTab] = useState('manual');
  const [mcqTemplateFileName, setMcqTemplateFileName] = useState('');
  const [mcqTemplateRows, setMcqTemplateRows] = useState([]);
  const [mcqTemplateErrors, setMcqTemplateErrors] = useState([]);
  const [mcqTemplateSubmitting, setMcqTemplateSubmitting] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [activeShareExam, setActiveShareExam] = useState(null);
  const [sharedBatches, setSharedBatches] = useState([]);
  const [shareableBatches, setShareableBatches] = useState([]);
  const [selectedShareBatchOptions, setSelectedShareBatchOptions] = useState([]);
  const [editingShareBatchId, setEditingShareBatchId] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareBatchesLoading, setShareBatchesLoading] = useState(false);
  const [shareSubmitting, setShareSubmitting] = useState(false);
  const [shareSearch, setShareSearch] = useState('');
  const [attachmentInputKey, setAttachmentInputKey] = useState(0);

  const canDelete = useMemo(() => {
    try {
      const raw = localStorage.getItem('userData');
      if (!raw) return false;
      const parsed = JSON.parse(raw);

      if (Boolean(parsed?.superAdmin?.superAdminId)) {
        return true;
      }

      const staff = parsed?.superAdmin?.staff || {};
      const candidates = [staff.role, staff.staffType, staff.type, parsed?.superAdmin?.role];
      return candidates.some((value) => {
        const normalized = String(value || '').toLowerCase().replace(/\s+/g, '');
        return normalized === 'admin' || normalized === 'administrator';
      });
    } catch (_) {
      return false;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadCoordinateCourses = async () => {
      setLoadingCourses(true);
      setError('');
      try {
        const response = await axiosInstance.get('/admin/me/coordinate-courses');
        if (cancelled || !response?.data) return;

        const data = response.data || {};
        const rawCourses = Object.values(data.coordinateCourses || {});

        const normalizedCourses = rawCourses
          .map((course) => {
            const courseId = Number(course?.courseId);
            if (!Number.isInteger(courseId)) return null;

            const shortName = String(course?.shortName || course?.courseShortName || '').trim();
            const fullName = String(course?.fullName || course?.courseName || '').trim();
            if (!fullName) return null;

            return {
              courseId,
              shortName,
              fullName,
              isDelete: Boolean(course?.isDelete),
            };
          })
          .filter(Boolean)
          .sort((a, b) => {
            const aLabel = (a.shortName || a.fullName).toLowerCase();
            const bLabel = (b.shortName || b.fullName).toLowerCase();
            return aLabel.localeCompare(bLabel);
          });

        setCoordinateCourses(normalizedCourses);
      } catch (err) {
        if (!cancelled) {
          setCoordinateCourses([]);
          setError(err?.response?.data?.message || 'Failed to load coordinated courses.');
        }
      } finally {
        if (!cancelled) {
          setLoadingCourses(false);
        }
      }
    };

    loadCoordinateCourses();

    return () => {
      cancelled = true;
    };
  }, []);

  const courseOptions = useMemo(() => {
    const seenByName = new Map();
    coordinateCourses.forEach((course) => {
      const key = course.fullName.trim().toLowerCase();
      if (!key || seenByName.has(key)) {
        if (key && seenByName.has(key) && course.isDelete) {
          const existing = seenByName.get(key);
          seenByName.set(key, { ...existing, canDelete: true });
        }
        return;
      }
      seenByName.set(key, {
        value: course.fullName,
        label: course.shortName ? `${course.shortName} - ${course.fullName}` : course.fullName,
        canDelete: Boolean(course.isDelete),
      });
    });
    return Array.from(seenByName.values());
  }, [coordinateCourses]);

  const canDeleteActions = useMemo(() => canDelete || Boolean(selectedCourse?.canDelete), [canDelete, selectedCourse]);

  const loadExams = useCallback(async (courseName) => {
    if (!courseName) {
      setExams([]);
      return;
    }

    setLoadingExams(true);
    setError('');
    try {
      const response = await axiosInstance.get('/admin/online-exams', {
        params: { courseName },
      });
      setExams(Array.isArray(response?.data?.exams) ? response.data.exams : []);
    } catch (err) {
      setExams([]);
      setError(err?.response?.data?.message || 'Failed to load online exams.');
    } finally {
      setLoadingExams(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedCourse?.value) {
      setExams([]);
      setEditingName('');
      setForm(EMPTY_FORM);
      return;
    }
    loadExams(selectedCourse.value);
  }, [selectedCourse, loadExams]);

  const filteredExams = useMemo(() => {
    const token = searchTerm.trim().toLowerCase();
    if (!token) {
      return exams;
    }

    return exams.filter((exam) => (
      String(exam.name || '').toLowerCase().includes(token) ||
      String(exam.description || '').toLowerCase().includes(token)
    ));
  }, [exams, searchTerm]);

  const resetForm = useCallback(() => {
    setEditingName('');
    setForm(EMPTY_FORM);
    setSelectedAttachmentFile(null);
    setSelectedAttachmentName('');
    setAttachmentInputKey((prev) => prev + 1);
  }, []);

  const handleRemoveExamAttachment = useCallback(() => {
    setSelectedAttachmentFile(null);
    setSelectedAttachmentName('');
    setForm((prev) => ({ ...prev, attachment: '' }));
    setAttachmentInputKey((prev) => prev + 1);
  }, []);

  const buildAttachmentHref = useCallback((attachmentPath) => {
    if (!attachmentPath) {
      return null;
    }
    if (/^https?:\/\//i.test(attachmentPath)) {
      return attachmentPath;
    }
    let baseUrl = String(axiosInstance.defaults.baseURL || '').replace(/\/$/, '');
    if (!baseUrl) {
      return attachmentPath;
    }

    // Avoid duplicated /api when baseURL already ends with /api and stored path starts with /api.
    if (/\/api$/i.test(baseUrl) && /^\/api\//i.test(attachmentPath)) {
      baseUrl = baseUrl.replace(/\/api$/i, '');
    }

    return `${baseUrl}${attachmentPath.startsWith('/') ? '' : '/'}${attachmentPath}`;
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedCourse?.value) {
      setError('Select a coordinated course first.');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      if (editingName) {
        const payload = {
          courseName: selectedCourse.value,
          description: form.description.trim() || null,
          attachment: form.attachment.trim() || null,
          marksForMcq: form.marksForMcq,
          minutes: form.minutes,
        };

        await axiosInstance.put('/admin/online-exams/bulk', {
          ...payload,
          currentName: editingName,
          newName: form.name,
        });
        setSuccess('Online exam updated in bulk for all matching course IDs.');
      } else {
        const formData = new FormData();
        formData.append('courseName', selectedCourse.value);
        formData.append('name', form.name);
        formData.append('description', form.description.trim());
        formData.append('marksForMcq', form.marksForMcq);
        formData.append('minutes', form.minutes);
        if (selectedAttachmentFile) {
          formData.append('attachment', selectedAttachmentFile);
        }

        await axiosInstance.post('/admin/online-exams', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        setSuccess('Online exam created in bulk for all matching course IDs.');
      }

      resetForm();
      await loadExams(selectedCourse.value);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save online exam.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (exam) => {
    setEditingName(String(exam.name || ''));
    setForm({
      name: String(exam.name || ''),
      description: String(exam.description || ''),
      attachment: String(exam.attachment || ''),
      marksForMcq: String(exam.marksForMcq ?? ''),
      minutes: String(exam.minutes ?? ''),
    });
    setSelectedAttachmentFile(null);
    setSelectedAttachmentName('');
    setSuccess('');
    setError('');
  };

  const handleDelete = async (examName) => {
    if (!selectedCourse?.value || !examName) {
      return;
    }

    const result = await Swal.fire({
      title: 'Delete Online Exam?',
      text: `Delete "${examName}" from all course IDs with the selected course name?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc2626',
    });

    if (!result.isConfirmed) {
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      await axiosInstance.delete('/admin/online-exams/bulk', {
        data: {
          courseName: selectedCourse.value,
          name: examName,
        },
      });
      setSuccess('Online exam deleted in bulk for all matching course IDs.');
      if (editingName && editingName === examName) {
        resetForm();
      }
      await loadExams(selectedCourse.value);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to delete online exam.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetMcqForm = useCallback(() => {
    setMcqForm(EMPTY_MCQ_FORM);
    setEditingMcqBulkKey('');
  }, []);

  const loadMcqs = useCallback(async (courseName, examName) => {
    if (!courseName || !examName) {
      setMcqs([]);
      return;
    }

    setMcqLoading(true);
    try {
      const response = await axiosInstance.get('/admin/online-exams/mcq', {
        params: {
          courseName,
          examName,
        },
      });
      setMcqs(Array.isArray(response?.data?.mcqs) ? response.data.mcqs : []);
    } catch (err) {
      setMcqs([]);
      setError(err?.response?.data?.message || 'Failed to load MCQs.');
    } finally {
      setMcqLoading(false);
    }
  }, []);

  const openMcqModal = async (exam) => {
    if (!selectedCourse?.value || !exam?.name) {
      setError('Select a coordinated course and exam first.');
      return;
    }

    setActiveMcqExam(exam);
    setIsMcqModalOpen(true);
    setMcqManagerTab('manual');
    resetMcqForm();
    setMcqTemplateFileName('');
    setMcqTemplateRows([]);
    setMcqTemplateErrors([]);
    await loadMcqs(selectedCourse.value, exam.name);
  };

  const closeMcqModal = () => {
    setIsMcqModalOpen(false);
    setActiveMcqExam(null);
    setMcqs([]);
    resetMcqForm();
    setSelectedMcqImageName('');
    setMcqManagerTab('manual');
    setMcqTemplateFileName('');
    setMcqTemplateRows([]);
    setMcqTemplateErrors([]);
  };

  const handleEditMcq = (mcq) => {
    setMcqManagerTab('manual');
    setEditingMcqBulkKey(String(mcq.bulkKey || ''));
    setMcqForm({
      question: String(mcq.question || ''),
      questionImage: String(mcq.questionImage || ''),
      answer1: String(mcq.answers?.[0] || ''),
      answer2: String(mcq.answers?.[1] || ''),
      answer3: String(mcq.answers?.[2] || ''),
      answer4: String(mcq.answers?.[3] || ''),
      answer5: String(mcq.answers?.[4] || ''),
      correctAnswer: String(mcq.correctAnswer || '1'),
    });
    setSelectedMcqImageName('');
  };

  const extractTemplateValue = (row, candidates) => {
    for (const key of candidates) {
      if (Object.prototype.hasOwnProperty.call(row, key)) {
        return row[key];
      }
    }
    return '';
  };

  const handleSelectMcqTemplateFile = async (file) => {
    if (!file) {
      setMcqTemplateFileName('');
      setMcqTemplateRows([]);
      setMcqTemplateErrors([]);
      return;
    }

    setMcqTemplateFileName(file.name);
    setMcqTemplateRows([]);
    setMcqTemplateErrors([]);
    setError('');
    setSuccess('');

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];
      if (!firstSheetName) {
        setMcqTemplateErrors(['Template file has no worksheet.']);
        return;
      }

      const worksheet = workbook.Sheets[firstSheetName];
      const rawRows = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
      const parsedRows = [];
      const errors = [];

      rawRows.forEach((row, index) => {
        const question = String(extractTemplateValue(row, ['question', 'Question']) || '').trim();
        const questionImage = String(extractTemplateValue(row, ['question_image', 'questionImage', 'Question Image']) || '').trim();
        const answer1 = String(extractTemplateValue(row, ['answer_1', 'answer1', 'Answer 1']) || '').trim();
        const answer2 = String(extractTemplateValue(row, ['answer_2', 'answer2', 'Answer 2']) || '').trim();
        const answer3 = String(extractTemplateValue(row, ['answer_3', 'answer3', 'Answer 3']) || '').trim();
        const answer4 = String(extractTemplateValue(row, ['answer_4', 'answer4', 'Answer 4']) || '').trim();
        const answer5 = String(extractTemplateValue(row, ['answer_5', 'answer5', 'Answer 5']) || '').trim();
        const correctRaw = extractTemplateValue(row, ['correct_answer', 'correctAnswer', 'Correct Answer']);
        const correctAnswer = Number.parseInt(String(correctRaw).trim(), 10);

        const isCompletelyEmpty = !question && !questionImage && !answer1 && !answer2 && !answer3 && !answer4 && !answer5 && !String(correctRaw).trim();
        if (isCompletelyEmpty) {
          return;
        }

        const rowNumber = index + 2;
        if (!question) {
          errors.push(`Row ${rowNumber}: question is required.`);
        }
        if (!answer1) {
          errors.push(`Row ${rowNumber}: answer_1 is required.`);
        }
        if (!answer2) {
          errors.push(`Row ${rowNumber}: answer_2 is required.`);
        }
        if (!Number.isInteger(correctAnswer) || correctAnswer < 1 || correctAnswer > 5) {
          errors.push(`Row ${rowNumber}: correct_answer must be between 1 and 5.`);
        }

        const answers = [answer1, answer2, answer3, answer4, answer5];
        if (Number.isInteger(correctAnswer) && correctAnswer >= 1 && correctAnswer <= 5 && !answers[correctAnswer - 1]) {
          errors.push(`Row ${rowNumber}: correct_answer points to an empty answer option.`);
        }

        parsedRows.push({
          question,
          question_image: questionImage || null,
          answer_1: answer1,
          answer_2: answer2,
          answer_3: answer3 || null,
          answer_4: answer4 || null,
          answer_5: answer5 || null,
          correct_answer: Number.isInteger(correctAnswer) ? correctAnswer : null,
        });
      });

      setMcqTemplateRows(parsedRows);
      setMcqTemplateErrors(errors);

      if (parsedRows.length === 0) {
        setError('No MCQ rows found in selected template.');
      } else if (errors.length === 0) {
        setSuccess(`Template parsed successfully with ${parsedRows.length} row(s).`);
      }
    } catch (parseError) {
      setMcqTemplateRows([]);
      setMcqTemplateErrors(['Failed to parse template file. Please verify format and columns.']);
    }
  };

  const handleUploadMcqTemplateRows = async () => {
    if (!selectedCourse?.value || !activeMcqExam?.name) {
      setError('Select coordinated course and exam before template upload.');
      return;
    }

    if (mcqTemplateRows.length === 0) {
      setError('No valid rows found to upload.');
      return;
    }

    if (mcqTemplateErrors.length > 0) {
      setError('Fix template row errors before uploading.');
      return;
    }

    setMcqTemplateSubmitting(true);
    setError('');
    setSuccess('');

    try {
      await axiosInstance.post('/admin/online-exams/mcq/bulk', {
        courseName: selectedCourse.value,
        examName: activeMcqExam.name,
        rows: mcqTemplateRows,
      });

      setSuccess(`Bulk uploaded ${mcqTemplateRows.length} MCQ row(s) successfully.`);
      setMcqTemplateFileName('');
      setMcqTemplateRows([]);
      setMcqTemplateErrors([]);
      await loadMcqs(selectedCourse.value, activeMcqExam.name);
    } catch (err) {
      const responseRowErrors = Array.isArray(err?.response?.data?.rowErrors)
        ? err.response.data.rowErrors.map((item) => `Row ${item.row}: ${Array.isArray(item.errors) ? item.errors.join(', ') : 'Invalid row'}`)
        : [];
      if (responseRowErrors.length > 0) {
        setMcqTemplateErrors(responseRowErrors);
      }
      setError(err?.response?.data?.message || 'Failed to upload MCQ template rows.');
    } finally {
      setMcqTemplateSubmitting(false);
    }
  };

  const handleUploadMcqQuestionImage = useCallback(async (file) => {
    if (!file) {
      return;
    }

    if (!selectedCourse?.value || !activeMcqExam?.name) {
      setError('Select coordinated course and exam before uploading question image.');
      return;
    }

    const data = new FormData();
    data.append('courseName', selectedCourse.value);
    data.append('examName', activeMcqExam.name);
    data.append('questionImageFile', file);

    setMcqImageUploading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axiosInstance.post('/admin/online-exams/mcq/question-image', data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const uploadedPath = String(response?.data?.questionImage || '').trim();
      if (!uploadedPath) {
        setError('Image upload completed but image path was not returned.');
        return;
      }

      setMcqForm((prev) => ({ ...prev, questionImage: uploadedPath }));
      setSelectedMcqImageName(file.name);
      setSuccess('Question image uploaded successfully.');
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to upload question image.');
    } finally {
      setMcqImageUploading(false);
    }
  }, [activeMcqExam?.name, selectedCourse?.value]);

  const handlePasteMcqQuestionImage = useCallback((event) => {
    if (mcqImageUploading) {
      return;
    }

    const clipboardItems = Array.from(event.clipboardData?.items || []);
    const imageItem = clipboardItems.find((item) => String(item.type || '').startsWith('image/'));

    if (!imageItem) {
      return;
    }

    const clipboardFile = imageItem.getAsFile();
    if (!clipboardFile) {
      setError('Clipboard image could not be read. Please try selecting the file manually.');
      return;
    }

    event.preventDefault();

    const mimeType = String(clipboardFile.type || 'image/png').toLowerCase();
    const extension = mimeType.includes('jpeg')
      ? 'jpg'
      : mimeType.includes('webp')
        ? 'webp'
        : 'png';

    const file = clipboardFile.name
      ? clipboardFile
      : new File([clipboardFile], `question-image-${Date.now()}.${extension}`, { type: mimeType });

    setSelectedMcqImageName(file.name);
    handleUploadMcqQuestionImage(file);
  }, [handleUploadMcqQuestionImage, mcqImageUploading]);

  const handleRemoveMcqQuestionImage = useCallback(async () => {
    const currentImage = String(mcqForm.questionImage || '').trim();
    if (!currentImage) {
      return;
    }

    if (!selectedCourse?.value || !activeMcqExam?.name) {
      setError('Select coordinated course and exam before removing question image.');
      return;
    }

    setMcqImageUploading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axiosInstance.delete('/admin/online-exams/mcq/question-image', {
        data: {
          courseName: selectedCourse.value,
          examName: activeMcqExam.name,
          bulkKey: editingMcqBulkKey || null,
          questionImage: currentImage,
        },
      });

      setMcqForm((prev) => ({ ...prev, questionImage: '' }));
      setSelectedMcqImageName('');
      setSuccess(response?.data?.message || 'Question image removed successfully.');

      if (editingMcqBulkKey) {
        await loadMcqs(selectedCourse.value, activeMcqExam.name);
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to remove question image.');
    } finally {
      setMcqImageUploading(false);
    }
  }, [activeMcqExam?.name, editingMcqBulkKey, loadMcqs, mcqForm.questionImage, selectedCourse?.value]);

  const handleSaveMcq = async (event) => {
    event.preventDefault();

    if (!selectedCourse?.value || !activeMcqExam?.name) {
      setError('Select coordinated course and exam before managing MCQ.');
      return;
    }

    const payload = {
      courseName: selectedCourse.value,
      examName: activeMcqExam.name,
      question: mcqForm.question,
      questionImage: mcqForm.questionImage.trim() || null,
      answers: [
        mcqForm.answer1,
        mcqForm.answer2,
        mcqForm.answer3,
        mcqForm.answer4,
        mcqForm.answer5,
      ],
      correctAnswer: Number(mcqForm.correctAnswer),
    };

    setMcqSubmitting(true);
    setError('');
    setSuccess('');

    try {
      if (editingMcqBulkKey) {
        await axiosInstance.put(`/admin/online-exams/mcq/${encodeURIComponent(editingMcqBulkKey)}`, payload);
        setSuccess('MCQ updated successfully for all linked bulk exam rows.');
      } else {
        await axiosInstance.post('/admin/online-exams/mcq', payload);
        setSuccess('MCQ added successfully for all linked bulk exam rows.');
      }

      resetMcqForm();
      await loadMcqs(selectedCourse.value, activeMcqExam.name);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save MCQ.');
    } finally {
      setMcqSubmitting(false);
    }
  };

  const handleDeleteMcq = async (mcq) => {
    if (!selectedCourse?.value || !activeMcqExam?.name || !mcq?.bulkKey) {
      return;
    }

    if (mcq.isLegacy) {
      setError('Legacy MCQ rows cannot be bulk-deleted. Recreate the MCQ to manage it in bulk.');
      return;
    }

    const confirm = await Swal.fire({
      title: 'Delete MCQ?',
      text: 'This MCQ will be removed from all linked bulk exam rows.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc2626',
    });

    if (!confirm.isConfirmed) {
      return;
    }

    setMcqSubmitting(true);
    setError('');
    setSuccess('');
    try {
      await axiosInstance.delete(`/admin/online-exams/mcq/${encodeURIComponent(mcq.bulkKey)}`, {
        data: {
          courseName: selectedCourse.value,
          examName: activeMcqExam.name,
        },
      });

      if (editingMcqBulkKey && editingMcqBulkKey === mcq.bulkKey) {
        resetMcqForm();
      }
      setSuccess('MCQ deleted successfully from bulk exam rows.');
      await loadMcqs(selectedCourse.value, activeMcqExam.name);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to delete MCQ.');
    } finally {
      setMcqSubmitting(false);
    }
  };

  const handleDownloadMcqTemplate = useCallback(() => {
    const headers = [
      'question',
      'question_image',
      'answer_1',
      'answer_2',
      'answer_3',
      'answer_4',
      'answer_5',
      'correct_answer',
    ];

    const sampleRows = [
      {
        question: 'Which option is correct for this sample question?',
        question_image: '',
        answer_1: 'Option A',
        answer_2: 'Option B',
        answer_3: 'Option C',
        answer_4: '',
        answer_5: '',
        correct_answer: 2,
      },
    ];

    const worksheet = XLSX.utils.json_to_sheet(sampleRows, { header: headers });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'MCQ Template');

    const examLabel = String(activeMcqExam?.name || 'Online_Exam')
      .replace(/[^a-zA-Z0-9_-]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 60) || 'Online_Exam';

    XLSX.writeFile(workbook, `${examLabel}_MCQ_Template.xlsx`);
  }, [activeMcqExam]);

  const loadShareableBatches = useCallback(async (courseName) => {
    if (!courseName) {
      setShareableBatches([]);
      return;
    }

    setShareBatchesLoading(true);
    try {
      const response = await axiosInstance.get('/admin/online-exams/shared/batches', {
        params: { courseName },
      });
      setShareableBatches(Array.isArray(response?.data?.batches) ? response.data.batches : []);
    } catch (err) {
      setShareableBatches([]);
      setError(err?.response?.data?.message || 'Failed to load shareable batches.');
    } finally {
      setShareBatchesLoading(false);
    }
  }, []);

  const loadSharedBatches = useCallback(async (courseName, examName) => {
    if (!courseName || !examName) {
      setSharedBatches([]);
      return;
    }

    setShareLoading(true);
    try {
      const response = await axiosInstance.get('/admin/online-exams/shared', {
        params: {
          courseName,
          examName,
        },
      });
      setSharedBatches(Array.isArray(response?.data?.shared) ? response.data.shared : []);
    } catch (err) {
      setSharedBatches([]);
      setError(err?.response?.data?.message || 'Failed to load shared batches.');
    } finally {
      setShareLoading(false);
    }
  }, []);

  const openShareModal = async (exam) => {
    if (!selectedCourse?.value || !exam?.name) {
      setError('Select a coordinated course and exam first.');
      return;
    }

    setActiveShareExam(exam);
    setIsShareModalOpen(true);
    setSelectedShareBatchOptions([]);
    setEditingShareBatchId(null);
    await Promise.all([
      loadShareableBatches(selectedCourse.value),
      loadSharedBatches(selectedCourse.value, exam.name),
    ]);
  };

  const closeShareModal = () => {
    setIsShareModalOpen(false);
    setActiveShareExam(null);
    setSelectedShareBatchOptions([]);
    setEditingShareBatchId(null);
    setSharedBatches([]);
    setShareSearch('');
  };

  const filteredSharedBatches = useMemo(() => {
    const token = shareSearch.trim().toLowerCase();
    if (!token) return sharedBatches;
    return sharedBatches.filter((item) => {
      const fields = [
        item.batchInternalName,
        item.batchName,
        item.courseShortName,
        item.courseFullName,
        item.whatsappGroupName,
        item.status,
      ];
      return fields.some((f) => String(f || '').toLowerCase().includes(token));
    });
  }, [sharedBatches, shareSearch]);

  const alreadySharedBatchIds = useMemo(
    () => new Set(sharedBatches.map((item) => Number(item.batchId))),
    [sharedBatches]
  );

  const shareBatchOptionGroups = useMemo(() => {
    const grouped = new Map();
    shareableBatches.forEach((batch) => {
      const batchId = Number(batch.batchId);
      if (!Number.isInteger(batchId)) {
        return;
      }

      // When adding new shares, exclude already-shared batches.
      // When editing an existing share, keep the current batch so it stays selectable.
      if (!editingShareBatchId && alreadySharedBatchIds.has(batchId)) {
        return;
      }
      if (editingShareBatchId && alreadySharedBatchIds.has(batchId) && Number(editingShareBatchId) !== batchId) {
        return;
      }

      const courseLabel = String(batch.courseShortName || batch.courseFullName || 'Other Course').trim() || 'Other Course';
      const batchName = String(batch.batchInternalName || batch.batchName || '').trim() || `Batch ${batchId}`;
      const whatsapp = String(batch.whatsappGroupName || '').trim();
      const status = String(batch.status || '').trim();

      const pieces = [batchName];
      if (whatsapp) {
        pieces.push(`WhatsApp: ${whatsapp}`);
      }
      if (status) {
        pieces.push(`Status: ${status}`);
      }

      const option = {
        value: batchId,
        label: pieces.join(' | '),
      };

      if (!grouped.has(courseLabel)) {
        grouped.set(courseLabel, []);
      }
      grouped.get(courseLabel).push(option);
    });

    return Array.from(grouped.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([label, options]) => ({ label, options }));
  }, [shareableBatches, alreadySharedBatchIds, editingShareBatchId]);

  const flatShareBatchOptions = useMemo(
    () => shareBatchOptionGroups.flatMap((group) => group.options),
    [shareBatchOptionGroups]
  );

  const handleEditShare = (shared) => {
    const option = flatShareBatchOptions.find((item) => Number(item.value) === Number(shared.batchId));
    setEditingShareBatchId(Number(shared.batchId));
    setSelectedShareBatchOptions(option ? [option] : []);
  };

  const resetShareForm = () => {
    setSelectedShareBatchOptions([]);
    setEditingShareBatchId(null);
  };

  const handleSaveShare = async () => {
    if (!selectedCourse?.value || !activeShareExam?.name) {
      setError('Select coordinated course and exam before sharing.');
      return;
    }

    const batchIds = selectedShareBatchOptions
      .map((item) => Number(item?.value))
      .filter((id) => Number.isInteger(id));

    if (batchIds.length === 0) {
      setError('Select at least one batch.');
      return;
    }

    setShareSubmitting(true);
    setError('');
    setSuccess('');

    try {
      if (editingShareBatchId) {
        await axiosInstance.put(`/admin/online-exams/shared/${editingShareBatchId}`, {
          courseName: selectedCourse.value,
          examName: activeShareExam.name,
          newBatchId: batchIds[0],
        });
        setSuccess('Exam share updated successfully.');
      } else {
        await axiosInstance.post('/admin/online-exams/shared', {
          courseName: selectedCourse.value,
          examName: activeShareExam.name,
          batchIds,
        });
        setSuccess('Exam shared successfully.');
      }

      resetShareForm();
      await loadSharedBatches(selectedCourse.value, activeShareExam.name);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save exam share.');
    } finally {
      setShareSubmitting(false);
    }
  };

  const handleDeleteShare = async (shared) => {
    if (!selectedCourse?.value || !activeShareExam?.name || !shared?.batchId) {
      return;
    }

    const confirm = await Swal.fire({
      title: 'Delete Shared Batch?',
      text: `Remove share for ${shared.batchInternalName || shared.batchName || 'selected batch'}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, remove',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc2626',
    });

    if (!confirm.isConfirmed) {
      return;
    }

    setShareSubmitting(true);
    setError('');
    setSuccess('');

    try {
      await axiosInstance.delete(`/admin/online-exams/shared/${shared.batchId}`, {
        data: {
          courseName: selectedCourse.value,
          examName: activeShareExam.name,
        },
      });

      if (editingShareBatchId && Number(editingShareBatchId) === Number(shared.batchId)) {
        resetShareForm();
      }

      setSuccess('Shared batch removed successfully.');
      await loadSharedBatches(selectedCourse.value, activeShareExam.name);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to delete shared batch.');
    } finally {
      setShareSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="mx-auto w-full max-w-7xl space-y-6 py-6">
        <div className="rounded-3xl border border-emerald-100 bg-gradient-to-r from-emerald-50 via-white to-cyan-50 p-6 shadow-sm">
          <h1 className="text-3xl font-black tracking-tight text-slate-900">My Exam Workspace</h1>
          <p className="mt-2 text-sm text-slate-700">
            Manage online exams by Coordinated Course name. Create, update, and delete run in bulk across all course IDs with the same selected name.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-1">
            <label htmlFor="my-exam-course-select" className="mb-2 block text-sm font-semibold text-slate-800">
              Coordinated Course
            </label>
            <Select
              inputId="my-exam-course-select"
              options={courseOptions}
              value={selectedCourse}
              onChange={(option) => {
                setSelectedCourse(option || null);
                setSearchTerm('');
                setSuccess('');
                setError('');
              }}
              placeholder={loadingCourses ? 'Loading coordinated courses...' : 'Select coordinated course'}
              isDisabled={loadingCourses}
              isClearable
              noOptionsMessage={() => (loadingCourses ? 'Loading...' : 'No coordinated courses found')}
              classNamePrefix="react-select"
            />

            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-wide text-slate-500">Course Names</p>
                <p className="mt-1 text-2xl font-extrabold text-slate-900">{courseOptions.length}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-wide text-slate-500">Online Exams</p>
                <p className="mt-1 text-2xl font-extrabold text-slate-900">{exams.length}</p>
              </div>
            </div>

            <p className="mt-4 text-xs leading-relaxed text-slate-500">
              Schedule type is intentionally hidden here. Selection is by course name only, and each action maps that name to all matching course IDs.
            </p>

            <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3">
              <button
                type="button"
                onClick={handleDownloadMcqTemplate}
                className="w-full rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
              >
                Download MCQ Template
              </button>
              <p className="mt-2 text-[11px] text-emerald-800">
                Use this Excel template to prepare MCQ data in the correct format.
              </p>
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-2">
            <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-lg font-bold text-slate-900">
                {editingName ? `Edit Online Exam: ${editingName}` : 'Create Online Exam'}
              </h2>
              <div className="flex items-center gap-2">
                {editingName && canDeleteActions && (
                  <button
                    type="button"
                    onClick={() => handleDelete(editingName)}
                    disabled={submitting}
                    className="rounded-lg border border-rose-300 px-3 py-1.5 text-sm font-medium text-rose-700 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Delete
                  </button>
                )}
                {editingName && (
                  <button
                    type="button"
                    onClick={resetForm}
                    className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Cancel Edit
                  </button>
                )}
              </div>
            </div>

            <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="md:col-span-1">
                <label className="mb-1 block text-sm font-medium text-slate-700">Exam Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                  placeholder="Enter exam name"
                  required
                />
              </div>

              <div className="md:col-span-1">
                <label className="mb-1 block text-sm font-medium text-slate-700">Attachment (PDF/DOC/DOCX)</label>
                <input
                  key={attachmentInputKey}
                  type="file"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={(e) => {
                    const file = e.target.files && e.target.files.length > 0 ? e.target.files[0] : null;
                    setSelectedAttachmentFile(file);
                    setSelectedAttachmentName(file ? file.name : '');
                  }}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-slate-700 hover:file:bg-slate-200"
                />
                <div className="mt-2 text-xs text-slate-600">
                  {selectedAttachmentName ? (
                    <span className="inline-flex items-center gap-1.5">
                      <svg className="h-4 w-4 text-emerald-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path d="M4 2.5A1.5 1.5 0 015.5 1h5.879a1.5 1.5 0 011.06.44l2.121 2.12a1.5 1.5 0 01.44 1.061V17.5A1.5 1.5 0 0113.5 19h-8A1.5 1.5 0 014 17.5v-15z" />
                      </svg>
                      <span>{`Selected: ${selectedAttachmentName}`}</span>
                    </span>
                  ) : 'Attachment is optional. If selected, it will be uploaded when you save.'}
                </div>
                {(selectedAttachmentName || form.attachment) && (
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={handleRemoveExamAttachment}
                      className="rounded-md border border-rose-300 px-2.5 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-50"
                    >
                      Remove attachment
                    </button>
                  </div>
                )}
                {form.attachment && (
                  <div className="mt-2 text-xs text-slate-600">
                    Current attachment:{' '}
                    <a
                      href={buildAttachmentHref(form.attachment)}
                      target="_blank"
                      rel="noreferrer"
                      className="font-semibold text-emerald-700 underline underline-offset-2"
                    >
                      Open file
                    </a>
                  </div>
                )}
              </div>

              <div className="md:col-span-1">
                <label className="mb-1 block text-sm font-medium text-slate-700">Marks for MCQ</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.marksForMcq}
                  onChange={(e) => setForm((prev) => ({ ...prev, marksForMcq: e.target.value }))}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                  placeholder="0.00"
                  required
                />
              </div>

              <div className="md:col-span-1">
                <label className="mb-1 block text-sm font-medium text-slate-700">Minutes</label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={form.minutes}
                  onChange={(e) => setForm((prev) => ({ ...prev, minutes: e.target.value }))}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                  placeholder="60"
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className="mb-1 block text-sm font-medium text-slate-700">Description (optional)</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                  className="h-24 w-full resize-y rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                  placeholder="Describe the exam"
                />
              </div>

              <div className="md:col-span-2 flex flex-wrap items-center gap-3">
                <button
                  type="submit"
                  disabled={submitting || !selectedCourse?.value}
                  className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                >
                  {submitting ? 'Saving...' : editingName ? 'Bulk Update Exam' : 'Bulk Create Exam'}
                </button>
                {!selectedCourse?.value && (
                  <p className="text-xs text-amber-700">Select Coordinated Course first.</p>
                )}
                {!editingName && !selectedAttachmentFile && selectedCourse?.value && (
                  <p className="text-xs text-amber-700">Document is required for bulk create.</p>
                )}
              </div>
            </form>
          </section>
        </div>

        {(error || success) && (
          <div className={`rounded-xl border p-4 text-sm ${error ? 'border-rose-200 bg-rose-50 text-rose-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
            {error || success}
          </div>
        )}

        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-lg font-bold text-slate-900">Online Exams</h2>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by exam name or description"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 sm:max-w-xs"
            />
          </div>

          {loadingExams ? (
            <div className="flex items-center justify-center py-10">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-emerald-600 border-t-transparent" />
            </div>
          ) : !selectedCourse?.value ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-600">
              Select a coordinated course to view online exams.
            </div>
          ) : filteredExams.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-600">
              No online exams found for selected coordinated course.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">Name</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">MCQ Marks</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">Minutes</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">Instances</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">Updated</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {filteredExams.map((exam) => (
                    <tr key={exam.name}>
                      <td className="px-4 py-3 align-top">
                        <div className="font-semibold text-slate-900">{exam.name}</div>
                        {exam.description && (
                          <p className="mt-1 max-w-xl text-xs text-slate-600">{exam.description}</p>
                        )}
                        {exam.attachment && (
                          <a
                            href={buildAttachmentHref(exam.attachment)}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-1 inline-block text-xs font-semibold text-emerald-700 underline underline-offset-2"
                          >
                            View attachment
                          </a>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">{Number(exam.marksForMcq || 0).toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm text-slate-700">{exam.minutes}</td>
                      <td className="px-4 py-3 text-sm text-slate-700">{exam.instanceCount}</td>
                      <td className="px-4 py-3 text-xs text-slate-600">{exam.updatedAt ? new Date(exam.updatedAt).toLocaleString() : '-'}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleEdit(exam)}
                            className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => openMcqModal(exam)}
                            className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
                          >
                            Add MCQ
                          </button>
                          <button
                            type="button"
                            onClick={() => openShareModal(exam)}
                            className="rounded-md border border-cyan-300 bg-cyan-50 px-3 py-1.5 text-xs font-semibold text-cyan-700 hover:bg-cyan-100"
                          >
                            Share
                          </button>
                          {canDeleteActions && (
                            <button
                              type="button"
                              onClick={() => handleDelete(exam.name)}
                              disabled={submitting}
                              className="rounded-md border border-rose-300 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {!canDeleteActions && (
          <p className="text-xs text-slate-500">
            Delete is restricted to admin users only.
          </p>
        )}
        {loadingCourses && (
          <p className="text-xs text-slate-500">Loading coordinated courses...</p>
        )}

        {isMcqModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4">
            <div className="max-h-[90vh] w-full max-w-6xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
              <div className="border-b border-slate-200 bg-gradient-to-r from-emerald-50 via-white to-cyan-50 px-6 py-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-xl font-black tracking-tight text-slate-900">MCQ Manager</h3>
                    <p className="mt-1 text-sm text-slate-600">
                      Exam: <span className="font-semibold text-slate-800">{activeMcqExam?.name || '-'}</span>
                    </p>
                    <p className="text-xs text-slate-500">
                      Changes are applied across all bulk exam rows for the selected Coordinated Course.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={closeMcqModal}
                    className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Close
                  </button>
                </div>
              </div>

              <div className="grid max-h-[calc(90vh-96px)] grid-cols-1 gap-0 overflow-y-auto lg:grid-cols-5">
                <div className="border-b border-slate-200 p-5 lg:col-span-2 lg:border-b-0 lg:border-r">
                  <div className="mb-3 flex items-center justify-between">
                    <h4 className="text-sm font-bold uppercase tracking-wide text-slate-700">
                      MCQ Input
                    </h4>
                    {editingMcqBulkKey && (
                      <button
                        type="button"
                        onClick={resetMcqForm}
                        className="rounded-md border border-slate-300 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        Cancel Edit
                      </button>
                    )}
                  </div>

                  <div className="mb-3 grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setMcqManagerTab('manual')}
                      className={`rounded-lg border px-3 py-2 text-xs font-semibold ${mcqManagerTab === 'manual' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'}`}
                    >
                      Manual Entry
                    </button>
                    <button
                      type="button"
                      onClick={() => setMcqManagerTab('template')}
                      className={`rounded-lg border px-3 py-2 text-xs font-semibold ${mcqManagerTab === 'template' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'}`}
                    >
                      Template Upload
                    </button>
                  </div>

                  {mcqManagerTab === 'manual' && (
                  <form onSubmit={handleSaveMcq} className="space-y-3">
                    <div>
                      <label className="mb-1 block text-xs font-semibold text-slate-700">Question</label>
                      <textarea
                        value={mcqForm.question}
                        onChange={(e) => setMcqForm((prev) => ({ ...prev, question: e.target.value }))}
                        className="h-24 w-full resize-y rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                        placeholder="Enter question"
                        required
                      />
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-semibold text-slate-700">Question Image Upload (optional)</label>
                      <div
                        onPaste={handlePasteMcqQuestionImage}
                        tabIndex={0}
                        className="rounded-lg border border-slate-200 bg-slate-50 p-2.5 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                      >
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                          onChange={(e) => {
                            const file = e.target.files && e.target.files.length > 0 ? e.target.files[0] : null;
                            setSelectedMcqImageName(file ? file.name : '');
                            if (file) {
                              handleUploadMcqQuestionImage(file);
                            }
                          }}
                          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-xs file:mr-3 file:rounded-md file:border-0 file:bg-slate-200 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-slate-700 hover:file:bg-slate-300"
                        />
                        <p className="mt-1 text-[11px] text-slate-500">
                          {mcqImageUploading
                            ? 'Uploading image...'
                            : selectedMcqImageName
                              ? `Selected: ${selectedMcqImageName}`
                              : 'Image will be uploaded immediately after selection or paste. Click here and press Ctrl+V to paste from the clipboard.'}
                        </p>
                        {mcqForm.questionImage && (
                          <div className="mt-1 flex flex-wrap items-center gap-2">
                            <a
                              href={buildAttachmentHref(mcqForm.questionImage)}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-block text-xs font-semibold text-emerald-700 underline underline-offset-2"
                            >
                              Open uploaded image
                            </a>
                            <button
                              type="button"
                              onClick={handleRemoveMcqQuestionImage}
                              className="rounded-md border border-rose-300 px-2 py-0.5 text-xs font-semibold text-rose-700 hover:bg-rose-50"
                            >
                              Remove image
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div>
                        <label className="mb-1 block text-xs font-semibold text-slate-700">Answer 1</label>
                        <input
                          type="text"
                          value={mcqForm.answer1}
                          onChange={(e) => setMcqForm((prev) => ({ ...prev, answer1: e.target.value }))}
                          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                          required
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-semibold text-slate-700">Answer 2</label>
                        <input
                          type="text"
                          value={mcqForm.answer2}
                          onChange={(e) => setMcqForm((prev) => ({ ...prev, answer2: e.target.value }))}
                          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                          required
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-semibold text-slate-700">Answer 3 (optional)</label>
                        <input
                          type="text"
                          value={mcqForm.answer3}
                          onChange={(e) => setMcqForm((prev) => ({ ...prev, answer3: e.target.value }))}
                          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-semibold text-slate-700">Answer 4 (optional)</label>
                        <input
                          type="text"
                          value={mcqForm.answer4}
                          onChange={(e) => setMcqForm((prev) => ({ ...prev, answer4: e.target.value }))}
                          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="mb-1 block text-xs font-semibold text-slate-700">Answer 5 (optional)</label>
                        <input
                          type="text"
                          value={mcqForm.answer5}
                          onChange={(e) => setMcqForm((prev) => ({ ...prev, answer5: e.target.value }))}
                          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-semibold text-slate-700">Correct Answer</label>
                      <select
                        value={mcqForm.correctAnswer}
                        onChange={(e) => setMcqForm((prev) => ({ ...prev, correctAnswer: e.target.value }))}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                      >
                        <option value="1">Answer 1</option>
                        <option value="2">Answer 2</option>
                        <option value="3">Answer 3</option>
                        <option value="4">Answer 4</option>
                        <option value="5">Answer 5</option>
                      </select>
                    </div>

                    <button
                      type="submit"
                      disabled={mcqSubmitting || mcqTemplateSubmitting}
                      className="w-full rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                    >
                      {mcqSubmitting ? 'Saving MCQ...' : editingMcqBulkKey ? 'Update MCQ' : 'Add MCQ'}
                    </button>
                  </form>
                  )}

                  {mcqManagerTab === 'template' && (
                    <div className="space-y-3">
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <p className="text-xs text-slate-700">
                          Upload the downloaded template to insert MCQs in bulk for this exam.
                        </p>
                        <button
                          type="button"
                          onClick={handleDownloadMcqTemplate}
                          className="mt-2 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
                        >
                          Download Template
                        </button>
                      </div>

                      <div>
                        <label className="mb-1 block text-xs font-semibold text-slate-700">Template File (.xlsx/.xls/.csv)</label>
                        <input
                          type="file"
                          accept=".xlsx,.xls,.csv"
                          onChange={(e) => {
                            const file = e.target.files && e.target.files.length > 0 ? e.target.files[0] : null;
                            handleSelectMcqTemplateFile(file);
                          }}
                          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-xs file:mr-3 file:rounded-md file:border-0 file:bg-slate-200 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-slate-700 hover:file:bg-slate-300"
                        />
                        {mcqTemplateFileName && (
                          <p className="mt-1 text-[11px] text-slate-600">Selected: {mcqTemplateFileName}</p>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                          <p className="text-slate-500">Parsed Rows</p>
                          <p className="text-lg font-bold text-slate-900">{mcqTemplateRows.length}</p>
                        </div>
                        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                          <p className="text-amber-700">Validation Errors</p>
                          <p className="text-lg font-bold text-amber-800">{mcqTemplateErrors.length}</p>
                        </div>
                      </div>

                      {mcqTemplateErrors.length > 0 && (
                        <div className="max-h-36 overflow-y-auto rounded-md border border-rose-200 bg-rose-50 p-2.5 text-[11px] text-rose-700">
                          {mcqTemplateErrors.map((item, idx) => (
                            <p key={`tpl-err-${idx}`}>• {item}</p>
                          ))}
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={handleUploadMcqTemplateRows}
                        disabled={mcqTemplateSubmitting || mcqTemplateRows.length === 0 || mcqTemplateErrors.length > 0}
                        className="w-full rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                      >
                        {mcqTemplateSubmitting ? 'Uploading Template...' : 'Upload Template & Insert MCQs'}
                      </button>
                    </div>
                  )}
                </div>

                <div className="p-5 lg:col-span-3">
                  <div className="mb-3 flex items-center justify-between">
                    <h4 className="text-sm font-bold uppercase tracking-wide text-slate-700">MCQ List</h4>
                    <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                      {mcqs.length} item(s)
                    </span>
                  </div>

                  {mcqLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="h-9 w-9 animate-spin rounded-full border-4 border-emerald-600 border-t-transparent" />
                    </div>
                  ) : mcqs.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-600">
                      No MCQs yet. Add your first MCQ using the form.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {mcqs.map((mcq, index) => (
                        <div key={mcq.bulkKey} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                          <div className="mb-2 flex items-start justify-between gap-3">
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Question {index + 1}</p>
                              <h5 className="mt-1 text-sm font-semibold text-slate-900">{mcq.question}</h5>
                            </div>
                            <div className="inline-flex gap-2">
                              <button
                                type="button"
                                onClick={() => handleEditMcq(mcq)}
                                className="rounded-md border border-slate-300 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteMcq(mcq)}
                                disabled={mcqSubmitting || mcq.isLegacy}
                                className="rounded-md border border-rose-300 px-2.5 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
                                title={mcq.isLegacy ? 'Legacy MCQ cannot be bulk deleted. Recreate it once.' : 'Delete MCQ'}
                              >
                                Delete
                              </button>
                            </div>
                          </div>

                          {mcq.questionImage && (
                            <a
                              href={mcq.questionImage}
                              target="_blank"
                              rel="noreferrer"
                              className="mb-2 inline-block text-xs font-semibold text-emerald-700 underline underline-offset-2"
                            >
                              View question image
                            </a>
                          )}

                          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            {mcq.answers.map((answer, answerIndex) => (
                              <div
                                key={`${mcq.bulkKey}-${answerIndex}`}
                                className={`rounded-md border px-3 py-2 text-xs ${answerIndex + 1 === mcq.correctAnswer ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : 'border-slate-200 bg-slate-50 text-slate-700'}`}
                              >
                                <span className="font-semibold">{`Option ${answerIndex + 1}: `}</span>
                                <span>{answer || '-'}</span>
                              </div>
                            ))}
                          </div>

                          {mcq.isLegacy && (
                            <p className="mt-2 text-[11px] text-amber-700">
                              Legacy MCQ row: edit/delete in bulk is limited until recreated.
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {isShareModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4">
            <div className="max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
              <div className="border-b border-slate-200 bg-gradient-to-r from-cyan-50 via-white to-emerald-50 px-6 py-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-xl font-black tracking-tight text-slate-900">Share Exam</h3>
                    <p className="mt-1 text-sm text-slate-600">
                      Exam: <span className="font-semibold text-slate-800">{activeShareExam?.name || '-'}</span>
                    </p>
                    <p className="text-xs text-slate-500">
                      Select batch details from the dropdown. Sharing applies across all exam instances for this Coordinated Course.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={closeShareModal}
                    className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Close
                  </button>
                </div>
              </div>

              <div className="grid max-h-[calc(90vh-96px)] grid-cols-1 gap-0 overflow-y-auto lg:grid-cols-5">
                <div className="border-b border-slate-200 p-5 lg:col-span-2 lg:border-b-0 lg:border-r">
                  <div className="mb-3 flex items-center justify-between">
                    <h4 className="text-sm font-bold uppercase tracking-wide text-slate-700">
                      {editingShareBatchId ? 'Edit Share' : 'Add Share'}
                    </h4>
                    {editingShareBatchId && (
                      <button
                        type="button"
                        onClick={resetShareForm}
                        className="rounded-md border border-slate-300 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        Cancel Edit
                      </button>
                    )}
                  </div>

                  <div className="rounded-xl border border-cyan-200 bg-cyan-50 p-3">
                    <label className="mb-1 block text-xs font-semibold text-cyan-800">
                      Batch Dropdown
                    </label>
                    <Select
                      options={shareBatchOptionGroups}
                      value={editingShareBatchId ? (selectedShareBatchOptions[0] || null) : selectedShareBatchOptions}
                      onChange={(value) => {
                        if (editingShareBatchId) {
                          const one = Array.isArray(value) ? value[0] : value;
                          setSelectedShareBatchOptions(one ? [one] : []);
                          return;
                        }
                        setSelectedShareBatchOptions(Array.isArray(value) ? value : []);
                      }}
                      isMulti={!editingShareBatchId}
                      isDisabled={shareBatchesLoading || shareSubmitting}
                      isLoading={shareBatchesLoading}
                      placeholder={shareBatchesLoading ? 'Loading batches...' : (editingShareBatchId ? 'Select replacement batch' : 'Select one or more batches')}
                      noOptionsMessage={() => (shareBatchesLoading ? 'Loading...' : 'No batches found for selected course')}
                      classNamePrefix="react-select"
                    />
                    <p className="mt-2 text-[11px] text-cyan-700">
                      {editingShareBatchId
                        ? 'Pick one batch to replace current shared batch.'
                        : 'You can multi-select batches to share in one action.'}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={handleSaveShare}
                    disabled={shareSubmitting || shareBatchesLoading}
                    className="mt-3 w-full rounded-lg bg-cyan-600 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                  >
                    {shareSubmitting ? 'Saving Share...' : editingShareBatchId ? 'Update Share' : 'Share Exam'}
                  </button>
                </div>

                <div className="p-5 lg:col-span-3">
                  <div className="mb-3 flex items-center justify-between">
                    <h4 className="text-sm font-bold uppercase tracking-wide text-slate-700">Shared Batches</h4>
                    <span className="rounded-full bg-cyan-100 px-3 py-1 text-xs font-semibold text-cyan-700">
                      {filteredSharedBatches.length}/{sharedBatches.length} item(s)
                    </span>
                  </div>

                  <div className="mb-3">
                    <input
                      type="text"
                      value={shareSearch}
                      onChange={(e) => setShareSearch(e.target.value)}
                      placeholder="Search by batch name, course, WhatsApp, status…"
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-xs focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/20"
                    />
                  </div>

                  {shareLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="h-9 w-9 animate-spin rounded-full border-4 border-cyan-600 border-t-transparent" />
                    </div>
                  ) : sharedBatches.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-600">
                      This exam is not shared with any batch yet.
                    </div>
                  ) : filteredSharedBatches.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-600">
                      No shared batches match your search.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredSharedBatches.map((item) => (
                        <div key={`shared-${item.batchId}`} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                          <div className="mb-2 flex items-start justify-between gap-3">
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-wide text-cyan-700">Batch</p>
                              <h5 className="mt-1 text-sm font-semibold text-slate-900">{item.batchInternalName || item.batchName || `Batch ${item.batchId}`}</h5>
                            </div>
                            <div className="inline-flex gap-2">
                              <button
                                type="button"
                                onClick={() => handleEditShare(item)}
                                className="rounded-md border border-slate-300 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                              >
                                Edit
                              </button>
                              {canDeleteActions && (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteShare(item)}
                                  disabled={shareSubmitting}
                                  className="rounded-md border border-rose-300 px-2.5 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  Delete
                                </button>
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                              <span className="font-semibold">Course: </span>
                              {item.courseShortName || item.courseFullName || '-'}
                            </div>
                            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                              <span className="font-semibold">Status: </span>
                              {item.status || '-'}
                            </div>
                            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                              <span className="font-semibold">WhatsApp: </span>
                              {item.whatsappGroupName || '-'}
                            </div>
                            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                              <span className="font-semibold">Shared At: </span>
                              {item.sharedAt ? new Date(item.sharedAt).toLocaleString() : '-'}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        </div>
    </DashboardLayout>
  );
}
